
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Mail, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useLocalStorage } from '@/hooks/useLocalStorage';

const formSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  reason: z.string().min(10, { message: "Please provide a reason with at least 10 characters" }),
});

type FormValues = z.infer<typeof formSchema>;

const RequestAccessForm: React.FC = () => {
  const { toast } = useToast(); // Correctly using the hook inside the component
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestStatus, setRequestStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [accessRequests, setAccessRequests] = useLocalStorage<Array<{email: string, reason: string, date: string}>>('accessRequests', []);
  const [lastSubmittedEmail, setLastSubmittedEmail] = useState<string | null>(null);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      reason: '',
    },
  });
  
  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    setRequestStatus('idle');
    setErrorDetails(null);
    
    try {
      // First, try to save to Supabase
      let savedToSupabase = false;

      try {
        console.log('Attempting to save access request to Supabase...');
        
        const { error, data } = await supabase
          .from('access_requests')
          .insert({
            email: values.email,
            reason: values.reason,
            status: 'pending'
          })
          .select();
        
        if (error) {
          console.log('Supabase error:', error);
          // Check if the error is due to missing table
          if (error.message?.includes('does not exist') || error.code === '42P01') {
            console.log('Table does not exist, will fallback to localStorage');
            throw new Error('Table does not exist');
          } else {
            throw error;
          }
        } else {
          savedToSupabase = true;
          console.log('Successfully saved request to Supabase', data);
          setLastSubmittedEmail(values.email);
        }
      } catch (supabaseError) {
        console.log('Caught Supabase error, falling back to localStorage:', supabaseError);
        // Continue to localStorage fallback below
      }
      
      // If Supabase save failed, save to localStorage as fallback
      if (!savedToSupabase) {
        console.log('Using localStorage fallback...');
        // Save to localStorage as a fallback
        const newRequests = [...accessRequests, {
          email: values.email,
          reason: values.reason,
          date: new Date().toISOString()
        }];
        setAccessRequests(newRequests);
        
        console.log('Saved to localStorage:', newRequests);
        
        setRequestStatus('success');
        setErrorDetails("Your request has been saved locally while our database is being set up.");
        
        toast({
          title: "Request Received",
          description: "While our system is being set up, your request has been saved locally.",
        });
      } else {
        // Supabase save was successful
        setRequestStatus('success');
        toast({
          title: "Request Submitted",
          description: "Your access request has been submitted successfully. We'll contact you soon.",
        });
      }
      
      // Reset form regardless of save method
      form.reset();
    } catch (error) {
      console.error('Failed to submit access request:', error);
      setRequestStatus('error');
      setErrorDetails(error instanceof Error ? error.message : "Failed to submit your request");
      
      toast({
        title: "Request Failed", 
        description: error instanceof Error 
          ? `Error: ${error.message}` 
          : "There was a problem submitting your request. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="space-y-4">
      {requestStatus === 'success' && (
        <Alert className="bg-green-50 text-green-800 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <AlertTitle>Request Received</AlertTitle>
          <AlertDescription>
            Thank you for your interest! We'll review your request and get back to you soon.
            {lastSubmittedEmail && (
              <p className="mt-2 text-sm font-medium">Submission for: {lastSubmittedEmail}</p>
            )}
            {errorDetails && (
              <p className="mt-2 text-sm">{errorDetails}</p>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      {requestStatus === 'error' && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Submission Error</AlertTitle>
          <AlertDescription>
            {errorDetails || "Failed to submit your request. Please try again later."}
          </AlertDescription>
        </Alert>
      )}
      
      {requestStatus !== 'success' && (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="your@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Why would you like access?</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Please briefly describe why you're interested in access..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Tell us a bit about yourself and why you're interested in the beta.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              <Mail className="mr-2 h-4 w-4" />
              {isSubmitting ? 'Submitting...' : 'Send Request'}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
};

export default RequestAccessForm;
